import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
} from '@lb-classroom/ui';
import { X } from 'lucide-react';
import classnames from 'classnames';
import TextInput from '@components/UI/atoms/form/TextInput';
import Select from '@components/UI/atoms/form/Select';
import ModalMessage from '@components/UI/organisms/ModalMessage';
import type { EditEducationHistoryModalProps } from './edit-education-history-modal.types';

export const EditEducationHistoryModal = ({
  isNew,
  isSubmitting,
  hasChanges,
  degree,
  major,
  universityName,
  courseName,
  yearFrom,
  yearTo,
  yearOptions,
  degreeValidation,
  universityValidation,
  courseValidation,
  yearRangeValidation,
  message,
  messageType,
  onDegreeChange,
  onMajorChange,
  onUniversityNameChange,
  onCourseNameChange,
  onYearFromChange,
  onYearToChange,
  onSubmit,
  onClose,
}: EditEducationHistoryModalProps) => {
  const title = isNew ? 'Add Degree' : 'Edit Degree';
  const wrapperClass = classnames('max-w-lg mx-auto', {
    loading: isSubmitting,
  });

  const degreeProps = {
    placeholder: 'B.A.',
    className: 'education-degree',
    value: degree,
    stateType: 'degree',
    name: 'degree',
    type: 'text',
    updateHandler: (value: string) => onDegreeChange(value),
  };

  const universityProps = {
    placeholder: 'University of San Carlos',
    className: 'education-university',
    value: universityName,
    stateType: 'university_name',
    name: 'university',
    type: 'text',
    updateHandler: (value: string) => onUniversityNameChange(value),
  };

  const courseProps = {
    placeholder: 'Secondary Education',
    className: 'education-course',
    value: courseName,
    stateType: 'course_name',
    name: 'course',
    type: 'text',
    updateHandler: (value: string) => onCourseNameChange(value),
  };

  const majorProps = {
    placeholder: 'English',
    className: 'education-major',
    value: major,
    stateType: 'major',
    name: 'major',
    type: 'text',
    updateHandler: (value: string) => onMajorChange(value),
  };

  const yearFromProps = {
    options: yearOptions,
    name: 'education__year_from',
    className: 'education__year_from',
    id: 'education__year_from_select',
    value: yearFrom || '',
    updateHandler: (value: string | number) => {
      onYearFromChange(value === '' ? null : Number(value));
    },
    blockClass: true,
    validationResult: null,
    invalidLabel: false,
  };

  const yearToProps = {
    options: yearOptions,
    name: 'education__year_to',
    className: 'education__year_to',
    id: 'education__year_to_select',
    value: yearTo || '',
    updateHandler: (value: string | number) => {
      onYearToChange(value === '' ? null : Number(value));
    },
    blockClass: true,
    validationResult: null,
    invalidLabel: false,
  };

  return (
    <div className={wrapperClass}>
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <button type="button" className="modal-close" onClick={onClose}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <ModalMessage message={message} messageType={messageType} />
          <form
            method="put"
            className="report-issue-form"
            onSubmit={onSubmit}
            noValidate
          >
            <div>
              <div className="actions-wrapper mb-8">
                <label>University/College</label>
                <TextInput {...universityProps} />
                <label className="invalid">
                  {universityValidation.message}
                </label>
              </div>
              <div className="grid grid-cols-12 gap-4 mb-8">
                <div className="col-span-3">
                  <label>Degree</label>
                  <TextInput {...degreeProps} />
                  <label className="invalid">{degreeValidation.message}</label>
                </div>
                <div className="col-span-9">
                  <label>Course Name</label>
                  <TextInput {...courseProps} />
                  <label className="invalid">{courseValidation.message}</label>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4 mb-8">
                <div className="col-span-6">
                  <div className="message-wrapper mb-8">
                    <label>Major(optional)</label>
                    <TextInput {...majorProps} />
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-6">
                      <label>From</label>
                      <Select {...yearFromProps} />
                    </div>
                    <div className="col-span-6">
                      <label>To</label>
                      <Select {...yearToProps} />
                    </div>
                  </div>
                  <label className="invalid">
                    {yearRangeValidation.message}
                  </label>
                </div>
              </div>
            </div>
            <Separator className="my-4" />
            <div className="text-right">
              <Button
                size="lg"
                type="submit"
                disabled={isSubmitting || !hasChanges}
              >
                Save
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
